import {
    Badge,
    Button,
    Icon,
    Menu,
    MenuDivider,
    MenuHeading,
    MenuItem,
    MenuSection,
    Option,
    Options,
    Pill,
    Popover,
    stopEvent,
    Toggle,
    useCheck,
    useStorage,
    useVisibility,
} from '@fold-dev/core'
import { useEffect, useMemo, useRef } from 'react'
import { TbCalendar, TbChartBar, TbFilter, TbLayoutCards, TbMenu3, TbSquareRoundedCheck } from 'react-icons/tb'
import { useNavigate, useParams } from 'react-router'
import { logger, setCachedProjectView } from '../../helpers'
import { useProjectUsers, useWorkspaceUsers } from '../../hooks/api.hooks'
import { useAppStore } from '../../store/app'
import { FRONTEER_FILTERS, getAllFilters } from '../tasks'

export const Filter = ({ workspaceId, projectId, ...rest }) => {
    const assigned = useCheck(false)
    const dates = useCheck(false)
    const labels = useCheck(false)
    const { view } = useParams()
    const { getStorage, setStorage } = useStorage()
    const filterPopup = useVisibility(false)
    const actions = useAppStore((state) => state.actions)
    const project = useAppStore((state) => state.project)
    const workspace = useAppStore((state) => state.workspace)
    const fields = useMemo(
        () => (workspace.workspaceUserFields || {})[projectId] || {},
        [workspace.workspaceUserFields, projectId]
    )
    const someFieldsHidden = useMemo(() => {
        const fields = (workspace.workspaceUserFields || {})[projectId]
        if (!fields) return false
        return fields.assigned === false || fields.dates === false || fields.labels === false
    }, [workspace.workspaceUserFields, projectId])
    const projectUsersAPI = useProjectUsers()
    const workspaceUsersAPI = useWorkspaceUsers()
    const { sort, hideComplete } = useAppStore((state: any) => state.filters)
    const showBadge = useMemo(() => someFieldsHidden || hideComplete, [someFieldsHidden, hideComplete])
    const navigate = useNavigate()
    const shouldUpdate = useRef(false)
    const selectedViewOption = useMemo(() => {
        switch (view) {
            case 'list':
                return 0
            case 'board':
                return 1
            case 'calendar':
                return 2
            case 'timeline':
                return 3
            default:
                return -1
        }
    }, [view])

    const navigateToView = (view: string) => {
        setCachedProjectView(projectId, view)
        navigate(`/app/workspace/${workspaceId}/c/${projectId}/${view}`)
    }

    const handleViewChange = (index) => {
        switch (index) {
            case 0:
                return navigateToView('list')
            case 1:
                return navigateToView('board')
            case 2:
                return navigateToView('calendar')
            case 3:
                return navigateToView('timeline')
        }
    }

    const handleSortRemove = () => {
        actions.setFilters({ sort: null, hideComplete })
        const updatedFilters = { ...getAllFilters(), [projectId]: { sort: null, hideComplete } }
        setStorage(FRONTEER_FILTERS, JSON.stringify(updatedFilters))
        filterPopup.hide()
        actions.setMainLoading(true)
    }

    const handleSortChange = (sort) => {
        actions.setFilters({ sort, hideComplete })
        const updatedFilters = { ...getAllFilters(), [projectId]: { sort, hideComplete } }
        setStorage(FRONTEER_FILTERS, JSON.stringify(updatedFilters))
        filterPopup.hide()
        actions.setMainLoading(true)
    }

    const handleHideCompleteUpdate = () => {
        actions.setFilters({ sort, hideComplete: !hideComplete })
        const updatedFilters = { ...getAllFilters(), [projectId]: { sort, hideComplete: !hideComplete } }
        setStorage(FRONTEER_FILTERS, JSON.stringify(updatedFilters))
        //filterPopup.hide()
        actions.setMainLoading(true)
    }

    const updatedField = async (field, value) => {
        const fields = {
            assigned: assigned.checked,
            dates: dates.checked,
            labels: labels.checked,
        }
        fields[field] = value

        shouldUpdate.current = true
        actions.workspaceUserUpdate({
            workspaceUserFields: { ...workspace.workspaceUserFields, [projectId]: { ...fields } },
        })
    }

    const dismissPopover = (e) => {
        try {
            if (shouldUpdate.current) {
                workspaceUsersAPI.update(workspaceId, workspace.workspaceUserId, {
                    fields: {
                        ...workspace.workspaceUserFields,
                        [projectId]: {
                            assigned: assigned.checked,
                            dates: dates.checked,
                            labels: labels.checked,
                        },
                    },
                })
            }
            filterPopup.hide()
            shouldUpdate.current = false
        } catch (e) {
            logger(e)
        }
    }

    useEffect(() => {
        if (filterPopup.visible && Object.keys(fields).length != 0) {
            assigned.setChecked(!!fields?.assigned)
            dates.setChecked(!!fields?.dates)
            labels.setChecked(!!fields?.labels)
        } else {
            assigned.setChecked(true)
            dates.setChecked(true)
            labels.setChecked(true)
        }
    }, [fields, filterPopup.visible])

    return (
        <>
            {selectedViewOption == 3 && (
                <Pill
                    subtle
                    colorToken="accent-50"
                    bg="linear-gradient(165deg, var(--f-color-indigo-600), var(--f-color-purple-500))"
                    size="sm">
                    PREVIEW
                </Pill>
            )}
            <Popover
                arrow
                width="fit-content"
                anchor="bottom-right"
                content={
                    <Menu
                        border="0"
                        width={250}>
                        <MenuHeading>Project View</MenuHeading>
                        <MenuSection>
                            <Options
                                shadow="none"
                                width="100%"
                                height="4rem"
                                selected={selectedViewOption}
                                onOptionChange={handleViewChange}>
                                <Option>
                                    <Icon
                                        icon={TbSquareRoundedCheck}
                                        size="lg"
                                    />
                                </Option>
                                <Option>
                                    <Icon
                                        style={{ transform: 'rotateZ(180deg)' }}
                                        icon={TbChartBar}
                                        size="lg"
                                    />
                                </Option>
                                <Option>
                                    <Icon
                                        className="tabler-calendar-icon"
                                        icon={TbCalendar}
                                        size="lg"
                                    />
                                </Option>
                                <Option
                                    display={
                                        window.location.href.includes('80bfb4c4-f212-4dc4-a8e4-b60a27d6531e')
                                            ? undefined
                                            : 'none'
                                    }>
                                    <Icon
                                        icon={TbMenu3}
                                        size="lg"
                                    />
                                </Option>
                            </Options>
                        </MenuSection>
                        <MenuDivider />
                        <MenuHeading>Visible Fields</MenuHeading>
                        <MenuItem
                            onClick={(e) => {
                                stopEvent(e)
                                assigned.check()
                                updatedField('assigned', !assigned.checked)
                            }}
                            prefix={
                                <Toggle
                                    size="sm"
                                    id="closed"
                                    on={assigned.checked}
                                    onChange={assigned.check}
                                />
                            }>
                            Assigned
                        </MenuItem>
                        <MenuItem
                            onClick={(e) => {
                                stopEvent(e)
                                dates.check()
                                updatedField('dates', !dates.checked)
                            }}
                            prefix={
                                <Toggle
                                    size="sm"
                                    id="closed"
                                    on={dates.checked}
                                    onChange={dates.check}
                                />
                            }>
                            Dates
                        </MenuItem>
                        <MenuItem
                            onClick={(e) => {
                                stopEvent(e)
                                labels.check()
                                updatedField('labels', !labels.checked)
                            }}
                            prefix={
                                <Toggle
                                    size="sm"
                                    id="closed"
                                    on={labels.checked}
                                    onChange={labels.check}
                                />
                            }>
                            Labels
                        </MenuItem>
                        <MenuDivider />
                        <MenuHeading>Completed Tasks</MenuHeading>
                        <MenuItem
                            onClick={(e) => {
                                stopEvent(e)
                                handleHideCompleteUpdate()
                            }}
                            prefix={
                                <Toggle
                                    size="sm"
                                    id="closed"
                                    on={hideComplete}
                                    onChange={handleHideCompleteUpdate}
                                />
                            }>
                            {hideComplete ? 'Show' : 'Hide'} completed
                        </MenuItem>
                        {/*   <MenuHeading>Fix Sort Order</MenuHeading>
                        <MenuItem
                            onClick={() => handleSortChange('title')}
                            active={sort == 'title'}
                            prefix={<Icon icon={TbFileText} />}>
                            By title
                        </MenuItem>
                        <MenuItem
                            onClick={() => handleSortChange('priority')}
                            active={sort == 'priority'}
                            prefix={<IconLib icon="flag" />}>
                            By priority
                        </MenuItem>
                        <MenuItem
                            onClick={() => handleSortChange('start')}
                            active={sort == 'start'}
                            prefix={<Icon icon={TbCalendarDot} />}>
                            By start date
                        </MenuItem>
                        <MenuItem
                            onClick={() => handleSortChange('end')}
                            active={sort == 'end'}
                            prefix={<Icon icon={TbCalendarDue} />}>
                            By end date
                        </MenuItem>
                        {!!sort && (
                            <>
                                <MenuDivider />
                                <MenuItem
                                    onClick={handleSortRemove}
                                    prefix={<Icon icon={TbX} />}>
                                    Clear ordering
                                </MenuItem>
                            </>
                        )}
                        <MenuDivider /> */}
                        {/*   <MenuItem
                            onClick={handleHideCompleteUpdate}
                            prefix={<Icon icon={hideComplete ? TbEye : TbEyeOff} />}>
                            {hideComplete ? 'Show' : 'Hide'} completed tasks
                        </MenuItem> */}
                    </Menu>
                }
                isVisible={filterPopup.visible}
                onDismiss={dismissPopover}>
                <Button
                    subtle
                    width={36}
                    height={36}
                    onClick={filterPopup.show}
                    {...rest}>
                    {showBadge && (
                        <Badge
                            anchor="top-right"
                            variant="accent"
                            style={{ outline: '2px solid var(--f-color-surface)' }}
                            zIndex={10}
                            //bgToken="danger"
                        />
                    )}
                    {/* <Icon
                        icon={selectedViewOption == 0 ? TbSquareRoundedCheck : selectedViewOption == 1 ? TbLayoutCards : TbCalendar}
                        size="lg"
                    /> */}
                    <Icon icon={TbFilter} />
                </Button>
            </Popover>
        </>
    )
}
